import { useEffect, useState } from 'preact/hooks';
import { api } from '../api/client';
import { showToast } from '../components/Toast';
import { T } from '../styles/tokens';
import { Button, Input } from '../components/ui';

interface DockerImage {
  id: string;
  name: string;
  registry_url: string;
  tag: string;
  description: string | null;
  fallback_url: string | null;
  is_default: number;
  is_active: number;
  created_at: string;
  updated_at: string;
}

interface FormData {
  name: string;
  registry_url: string;
  tag: string;
  description: string;
  fallback_url: string;
  is_default: boolean;
}

const EMPTY_FORM: FormData = { name: '', registry_url: '', tag: 'latest', description: '', fallback_url: '', is_default: false };

export function AdminDockerImagesPage() {
  const [images, setImages] = useState<DockerImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>({ ...EMPTY_FORM });

  const load = async () => {
    try {
      const res = await api.get<any>('/admin/docker-images?includeInactive=true');
      setImages((res as any)?.images || []);
    } catch (e: any) {
      showToast(e.message || 'Failed to load', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (!form.name || !form.registry_url) {
        showToast('Name and Registry URL are required', 'error');
        return;
      }
      if (editId) {
        await api.put<any>(`/admin/docker-images/${editId}`, form);
        showToast('Image updated', 'success');
      } else {
        await api.post<any>('/admin/docker-images', form);
        showToast('Image created', 'success');
      }
      setShowForm(false);
      setEditId(null);
      setForm({ ...EMPTY_FORM });
      load();
    } catch (e: any) {
      showToast(e.message || 'Failed to save', 'error');
    }
  };

  const setDefault = async (id: string) => {
    try {
      await api.put<any>(`/admin/docker-images/${id}/set-default`, {});
      showToast('Default image updated', 'success');
      load();
    } catch (e: any) {
      showToast(e.message || 'Failed', 'error');
    }
  };

  const deleteImage = async (id: string) => {
    if (!confirm('Deactivate this image?')) return;
    try {
      await api.delete<any>(`/admin/docker-images/${id}`);
      showToast('Image deactivated', 'success');
      load();
    } catch (e: any) {
      showToast(e.message || 'Failed', 'error');
    }
  };

  const openEdit = (img: DockerImage) => {
    setEditId(img.id);
    setForm({ name: img.name, registry_url: img.registry_url, tag: img.tag, description: img.description || '', fallback_url: img.fallback_url || '', is_default: !!img.is_default });
    setShowForm(true);
  };

  if (loading) return <div style={`padding:40px;color:${T.textSecondary};text-align:center`}>Loading...</div>;

  return (
    <div style="padding:24px;max-width:1200px;margin:0 auto">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:24px">
        <div>
 <h1 style={`color:${T.text};margin:0;font-size:24px`}>Docker Images</h1>
          <p style={`color:${T.textSecondary};margin:4px 0 0;font-size:14px`}>Manage container images for Cloud Machines provisioning</p>
        </div>
        <Button variant="success" onClick={() => { setEditId(null); setForm({ ...EMPTY_FORM }); setShowForm(true); }}>
          + New Image
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={`background:${T.bgSecondary};border:1px solid ${T.border};border-radius:8px;padding:20px;margin-bottom:24px`}>
          <h3 style={`color:${T.text};margin:0 0 16px`}>{editId ? 'Edit Image' : 'New Docker Image'}</h3>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
            <div>
              <Input label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="OpenClaw Official" />
            </div>
            <div>
              <Input label="Registry URL *" value={form.registry_url} onChange={(v) => setForm({ ...form, registry_url: v })} placeholder="ghcr.io/org/app" />
            </div>
            <div>
              <Input label="Tag" value={form.tag} onChange={(v) => setForm({ ...form, tag: v })} placeholder="latest" />
            </div>
            <div>
              <Input label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} placeholder="Official image from GHCR" />
            </div>
            <div style="grid-column:1/3">
              <Input label="Fallback URL (optional — used if primary pull fails)" value={form.fallback_url} onChange={(v) => setForm({ ...form, fallback_url: v })} placeholder="e.g. org/app:latest (Docker Hub fallback)" />
            </div>
          </div>
          {!editId && (
            <label style={`color:${T.textSecondary};font-size:13px;display:flex;align-items:center;gap:8px;margin-top:12px`}>
              <input type="checkbox" checked={form.is_default} onChange={(e: any) => setForm({ ...form, is_default: e.target.checked })} />
              Set as default image
            </label>
          )}
          <div style="display:flex;gap:8px;margin-top:16px">
            <Button variant="success" onClick={save}>
              {editId ? 'Update' : 'Create'}
            </Button>
            <Button variant="secondary" onClick={() => { setShowForm(false); setEditId(null); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={`background:${T.bgSecondary};border:1px solid ${T.border};border-radius:8px;overflow:hidden`}>
        <table style="width:100%;border-collapse:collapse">
          <thead>
            <tr style="border-bottom:1px solid #30363d">
              {['Name', 'Full Image URL', 'Default', 'Status', 'Created', 'Actions'].map(h => (
                <th key={h} style={`padding:12px 16px;text-align:left;color:${T.textSecondary};font-size:12px;text-transform:uppercase;font-weight:600`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {images.map(img => (
              <tr key={img.id} style={`border-bottom:1px solid #30363d;${!img.is_active ? 'opacity:0.5' : ''}`}>
                <td style="padding:12px 16px">
                  <div style={`color:${T.text};font-weight:500`}>{img.name}</div>
                  {img.description && <div style={`color:${T.textSecondary};font-size:12px;margin-top:2px`}>{img.description}</div>}
                </td>
                <td style="padding:12px 16px">
                  <code style={`background:${T.bg};padding:4px 8px;border-radius:4px;color:${T.primary};font-size:13px`}>
                    {img.registry_url}:{img.tag}
                  </code>
                </td>
                <td style="padding:12px 16px;text-align:center">
                  {img.is_default ? (
 <span title="Default" style="font-size:20px;cursor:default"></span>
                  ) : img.is_active ? (
 <Button variant="ghost" size="xs" title="Set as default" onClick={() => setDefault(img.id)} style={{ opacity: 0.4 }}></Button>
                  ) : null}
                </td>
                <td style="padding:12px 16px">
                  <span style={`display:inline-block;padding:2px 8px;border-radius:12px;font-size:12px;font-weight:500;${img.is_active ? 'background:#0d4429;color:#3fb950' : 'background:#3d1d00;color:#d29922'}`}>
                    {img.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={`padding:12px 16px;color:${T.textSecondary};font-size:13px`}>
                  {new Date(img.created_at + 'Z').toLocaleDateString()}
                </td>
                <td style="padding:12px 16px">
                  <div style="display:flex;gap:6px">
                    <Button variant="secondary" size="xs" onClick={() => openEdit(img)}>
                      Edit
                    </Button>
                    {!img.is_default && img.is_active && (
                      <Button variant="danger" size="xs" onClick={() => deleteImage(img.id)}>
                        Delete
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {images.length === 0 && (
              <tr><td colSpan={6} style={`padding:40px;text-align:center;color:${T.textSecondary}`}>No Docker images configured</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
